import { useRef } from 'react';
import { ClientOnly } from 'remix-utils';
import { useTracker } from '@archipro-website/tracker';
import { VideoPlayer } from '@rocco/components/video-player/video-player';
import { cn } from '@rocco/utils/cn';
import type { VideoColumnProps } from '@modules/page-builder/types';

interface RevampVideoModuleProps {
    column: VideoColumnProps;
    className?: string;
}

/**
 * Video module for the revamp body. Sits on the same measure and caption
 * treatment as {@link RevampImageFigure} so a video reads as one of the
 * article's figures rather than an embed dropped into the column.
 *
 * Logs the legacy `Video` event on play with the video id in `ExtraData`,
 * matching the legacy article's SingleVideoModule so the series is continuous.
 */
export const RevampVideoModule = ({
    column,
    className,
}: RevampVideoModuleProps) => {
    const tracker = useTracker();
    // Once per rendered player, not once per play. Legacy gets this from the
    // aria VideoPlayer, which guards `trackPlayEvent` behind its own ref; the
    // rocco player passes `onPlay` straight through, so every pause/resume
    // would otherwise inflate the series.
    const isPlayEventTracked = useRef(false);

    const videoURL = column.video?.videoURL;
    if (!videoURL) return null;

    const caption = column.video?.caption;
    const { width, height } = column.dimension ?? {};

    return (
        <figure
            className={cn('m-0 flex w-full flex-col gap-4 md:gap-6', className)}
        >
            <div
                className="relative w-full overflow-hidden rounded-tile bg-black"
                style={
                    width && height
                        ? { aspectRatio: `${width} / ${height}` }
                        : { aspectRatio: '16 / 9' }
                }
            >
                {/* react-player is CJS, so it only survives the client
                    bundle — rendering it on the server fails the whole page. */}
                <ClientOnly fallback={null}>
                    {() => (
                        <VideoPlayer
                            src={videoURL}
                            coverSrc={
                                column.video?.coverImage ??
                                column.video?.coverImageThumbnail
                            }
                            fill
                            controls
                            playsInline
                            onPlay={() => {
                                if (isPlayEventTracked.current) return;
                                isPlayEventTracked.current = true;
                                tracker.log('Video', {
                                    url: new URL(window.location.href),
                                    data: {
                                        // VideoID
                                        ExtraData: column.video?.id ?? 0,
                                    },
                                });
                            }}
                            // A failed embed (private/removed video) leaves the
                            // cover in place rather than breaking the flow.
                            onError={() => {}}
                        />
                    )}
                </ClientOnly>
            </div>
            {caption && (
                <figcaption className="m-0 font-sans text-[0.75rem] leading-[1.3] tracking-[-0.03em] text-black/80 md:w-[25rem] md:text-[0.87rem]">
                    {caption}
                </figcaption>
            )}
        </figure>
    );
};
